import React, { useState, useEffect, useRef } from 'react';
import { MdClose, MdArrowBack, MdArrowForward, MdQuiz, MdPeople, MdVisibility, MdAutorenew } from 'react-icons/md';
import Backdrop from 'components/Backdrop';
import Button from 'components/Button';

import './index.css';

const SLIDES = [
    {
        title: 'Vocab Quizzes',
        subtitle: 'A quick overview of the vocab quiz feature',
        content: (
            <div className='vqw__welcome'>
                <div className='vqw__welcomeIcon'><MdQuiz /></div>
                <p>Vocab quizzes test students on their assigned vocabulary words. Each quiz uses fill-in-the-blank sentences with 4 multiple choice options.</p>
                <div className='vqw__welcomeHighlights'>
                    <div className='vqw__welcomeItem'>
                        <span className='vqw__welcomeNumber'>4</span>
                        <span className='vqw__welcomeLabel'>unique quiz variants per set</span>
                    </div>
                    <div className='vqw__welcomeItem'>
                        <span className='vqw__welcomeNumber'>∞</span>
                        <span className='vqw__welcomeLabel'>unlimited retakes</span>
                    </div>
                    <div className='vqw__welcomeItem'>
                        <span className='vqw__welcomeNumber'>Free</span>
                        <span className='vqw__welcomeLabel'>for all students</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        title: 'Step 1: Students Take Quizzes',
        subtitle: 'Student side',
        content: (
            <div className='vqw__slide'>
                <p className='vqw__slideText'>Students see a "Take Quiz" button on each assigned vocab deck.</p>
                <div className='vqw__mockQuiz'>
                    <div className='vqw__mockQuizHeader'>
                        <span className='vqw__mockBadge'>Quiz 1</span>
                        <span>Question 3 of 20</span>
                    </div>
                    <div className='vqw__mockSentence'>
                        The professor's ____ lecture captivated the audience.
                    </div>
                    <div className='vqw__mockChoices'>
                        <div className='vqw__mockChoice'>A. eloquent</div>
                        <div className='vqw__mockChoice vqw__mockChoice--selected'>B. riveting</div>
                        <div className='vqw__mockChoice'>C. mundane</div>
                        <div className='vqw__mockChoice'>D. verbose</div>
                    </div>
                </div>
                <p className='vqw__slideNote'>Students can navigate with keyboard shortcuts (arrow keys, A-D, Enter) or by clicking.</p>
            </div>
        ),
    },
    {
        title: 'Step 2: Quiz Variants & Retakes',
        subtitle: 'How quiz cycling works',
        content: (
            <div className='vqw__slide'>
                <p className='vqw__slideText'>Each vocab set has 4 unique quiz variants with different sentences and distractors. Students cycle through them:</p>
                <div className='vqw__cycle'>
                    <div className='vqw__cycleItem vqw__cycleItem--active'>Quiz 1</div>
                    <span className='vqw__cycleArrow'>→</span>
                    <div className='vqw__cycleItem'>Quiz 2</div>
                    <span className='vqw__cycleArrow'>→</span>
                    <div className='vqw__cycleItem'>Quiz 3</div>
                    <span className='vqw__cycleArrow'>→</span>
                    <div className='vqw__cycleItem'>Quiz 4</div>
                    <span className='vqw__cycleArrow'>→</span>
                    <div className='vqw__cycleItem vqw__cycleItem--repeat'>Quiz 1</div>
                </div>
                <p className='vqw__slideText'>Students can take unlimited retakes. The variant cycles so they always get different questions. If they exit mid-quiz, they can save and resume later, or discard the attempt.</p>
            </div>
        ),
    },
    {
        title: 'Step 3: Automatic Flashcard Sorting',
        subtitle: 'Quiz results feed back into flashcards',
        content: (
            <div className='vqw__slide'>
                <div className='vqw__sortingDemo'>
                    <div className='vqw__sortBox vqw__sortBox--correct'>
                        <span className='vqw__sortLabel'>Correct answers</span>
                        <span className='vqw__sortArrow'>→</span>
                        <span className='vqw__sortPile vqw__sortPile--mastered'>Mastered pile</span>
                    </div>
                    <div className='vqw__sortBox vqw__sortBox--wrong'>
                        <span className='vqw__sortLabel'>Incorrect answers</span>
                        <span className='vqw__sortArrow'>→</span>
                        <span className='vqw__sortPile vqw__sortPile--learning'>Learning pile</span>
                    </div>
                </div>
                <p className='vqw__slideText'>After submitting a quiz, the student's flashcard decks are automatically updated. Words they got right move to Mastered, and words they missed move to Learning — creating a continuous study loop.</p>
            </div>
        ),
    },
    {
        title: 'Step 4: Track Progress',
        subtitle: 'Tutor side — monitor quiz results',
        content: (
            <div className='vqw__slide'>
                <p className='vqw__slideText'>You can track quiz results in two places:</p>
                <div className='vqw__methods'>
                    <div className='vqw__method'>
                        <MdVisibility className='vqw__methodIcon' />
                        <div>
                            <h4 className='vqw__methodTitle'>Student Vocab Tab</h4>
                            <p className='vqw__methodDesc'>Toggle to "Quiz Results" to see every quiz attempt, score, and click "Report" to view the full question-by-question breakdown.</p>
                        </div>
                    </div>
                    <div className='vqw__method'>
                        <MdQuiz className='vqw__methodIcon' />
                        <div>
                            <h4 className='vqw__methodTitle'>Quiz Preview</h4>
                            <p className='vqw__methodDesc'>On the "Vocab Quizzes" tab, preview any quiz variant to see all sentences, correct answers, and distractors before students take them.</p>
                        </div>
                    </div>
                </div>
            </div>
        ),
    },
];

function VocabQuizWalkthrough({ onClose }: { onClose: () => void }) {
    const [step, setStep] = useState(0);
    const dialogRef = useRef<HTMLDivElement>(null);

    const slide = SLIDES[step];
    const isFirst = step === 0;
    const isLast = step === SLIDES.length - 1;

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight' && !isLast) setStep(s => s + 1);
            else if (e.key === 'ArrowLeft' && !isFirst) setStep(s => s - 1);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isFirst, isLast, onClose]);

    useEffect(() => {
        dialogRef.current?.focus();
    }, []);

    return (
        <>
            <Backdrop />
            <div className='vqw' ref={dialogRef} tabIndex={-1} role='dialog' aria-label='Vocab quiz feature walkthrough'>
                <div className='vqw__header'>
                    <div>
                        <h1 className='vqw__title'>{slide.title}</h1>
                        {slide.subtitle && <p className='vqw__subtitle'>{slide.subtitle}</p>}
                    </div>
                    <button className='vqw__closeBtn' onClick={onClose} type='button'>
                        <MdClose />
                    </button>
                </div>

                <div className='vqw__body'>
                    {slide.content}
                </div>

                <div className='vqw__footer'>
                    <div className='vqw__dots'>
                        {SLIDES.map((_, i) => (
                            <button
                                key={i}
                                className={`vqw__dot ${i === step ? 'vqw__dot--active' : ''}`}
                                onClick={() => setStep(i)}
                                type='button'
                            />
                        ))}
                    </div>
                    <div className='vqw__nav'>
                        {!isFirst && (
                            <Button secondary onClick={() => setStep(step - 1)}>
                                <MdArrowBack style={{ verticalAlign: 'middle', marginRight: 4 }} /> Back
                            </Button>
                        )}
                        {isLast ? (
                            <Button onClick={onClose}>
                                Got it!
                            </Button>
                        ) : (
                            <Button onClick={() => setStep(step + 1)}>
                                Next <MdArrowForward style={{ verticalAlign: 'middle', marginLeft: 4 }} />
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default VocabQuizWalkthrough;
